import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useTutorialStore from '../../stores/tutorialStore';

const PADDING = 8;
const TOOLTIP_WIDTH = 340;
const TOOLTIP_OFFSET = 16;

function computeSpotlightRect(target) {
  if (!target) return null;
  const r = target.getBoundingClientRect();
  return {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  };
}

function computeTooltipPosition(spotlight, position) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!spotlight) {
    return {
      top: vh / 2 - 100,
      left: Math.max(16, vw / 2 - TOOLTIP_WIDTH / 2),
      centered: true,
    };
  }

  let top;
  let left;

  switch (position) {
    case 'top':
      top = spotlight.top - TOOLTIP_OFFSET - 10;
      left = spotlight.left + spotlight.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case 'left':
      top = spotlight.top + spotlight.height / 2 - 60;
      left = spotlight.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET;
      break;
    case 'right':
      top = spotlight.top + spotlight.height / 2 - 60;
      left = spotlight.left + spotlight.width + TOOLTIP_OFFSET;
      break;
    case 'bottom':
    default:
      top = spotlight.top + spotlight.height + TOOLTIP_OFFSET;
      left = spotlight.left + spotlight.width / 2 - TOOLTIP_WIDTH / 2;
      break;
  }

  // Clamp within viewport
  left = Math.max(16, Math.min(left, vw - TOOLTIP_WIDTH - 16));
  if (top < 16) {
    top = spotlight.top + spotlight.height + TOOLTIP_OFFSET;
  }
  if (top + 200 > vh) {
    top = Math.max(16, spotlight.top - TOOLTIP_OFFSET - 180);
  }

  return { top, left, centered: false };
}

const TutorialOverlay = () => {
  const isActive = useTutorialStore((s) => s.isActive);
  const activeTutorial = useTutorialStore((s) => s.activeTutorial);
  const nextStep = useTutorialStore((s) => s.nextStep);
  const prevStep = useTutorialStore((s) => s.prevStep);
  const skipTutorial = useTutorialStore((s) => s.skipTutorial);

  const [spotlight, setSpotlight] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0, centered: true });
  const [targetMissing, setTargetMissing] = useState(false);
  const rafRef = useRef(null);

  const step = activeTutorial?.steps?.[activeTutorial.currentIndex] || null;

  const reposition = () => {
    if (!step) return;
    const target = step.target ? document.querySelector(step.target) : null;
    if (!target) {
      setSpotlight(null);
      setTargetMissing(true);
      setTooltipPos(computeTooltipPosition(null, step.position));
      return;
    }
    setTargetMissing(false);
    const rect = computeSpotlightRect(target);
    setSpotlight(rect);
    setTooltipPos(computeTooltipPosition(rect, step.position));
  };

  useLayoutEffect(() => {
    if (!isActive || !step) return;
    const target = step.target ? document.querySelector(step.target) : null;
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
    // Delay initial measurement to allow scroll to settle
    const t = setTimeout(reposition, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, activeTutorial?.currentIndex, activeTutorial?.module]);

  useEffect(() => {
    if (!isActive) return undefined;

    const onResize = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(reposition);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') skipTutorial();
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    window.addEventListener('keydown', onKey);

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(onResize);
      observer.observe(document.body);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      window.removeEventListener('keydown', onKey);
      if (observer) observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, activeTutorial?.currentIndex]);

  if (!isActive || !activeTutorial || !step) return null;

  const total = activeTutorial.steps.length;
  const idx = activeTutorial.currentIndex;
  const isFirst = idx === 0;
  const isLast = idx === total - 1;

  return (
    <div className="tutorial-overlay-root">
      {spotlight && !targetMissing && (
        <div
          className="tutorial-spotlight"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
          }}
        />
      )}
      {(!spotlight || targetMissing) && <div className="tutorial-overlay-backdrop" />}

      <div
        className={`tutorial-tooltip ${tooltipPos.centered ? 'is-centered' : ''}`}
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
        role="dialog"
        aria-label="Tutorial step"
      >
        <div className="tutorial-tooltip-header">
          <h3 className="tutorial-tooltip-title">{step.title}</h3>
          <span className="tutorial-step-counter">Step {idx + 1} of {total}</span>
        </div>
        <p className="tutorial-tooltip-description">{step.description}</p>
        {targetMissing && (
          <p className="tutorial-tooltip-note">This element is not on the current screen.</p>
        )}
        <div className="tutorial-tooltip-actions">
          <button type="button" className="tutorial-btn tutorial-btn-skip" onClick={skipTutorial}>
            Skip Tutorial
          </button>
          <div className="tutorial-tooltip-nav">
            {!isFirst && (
              <button type="button" className="tutorial-btn tutorial-btn-back" onClick={prevStep}>
                Back
              </button>
            )}
            <button type="button" className="tutorial-btn tutorial-btn-next" onClick={nextStep}>
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialOverlay;
